import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import Field from '../field'
import useContactUsFields from './useContactUsFields'
import PhoneField from '../phone-field'
import {FormControl, FormHelperText, FormLabel, InputGroup, Text, Textarea} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {CONTACT_US_MESSAGE_MAXLENGTH} from '../../constants'
import useShipToCountry from '../../hooks/use-ship-to-country'

/**
 * ContactUsFields component renders a form with various input fields for contacting.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.form - The form object containing field configurations.
 * @param {string} [props.prefix=''] - A prefix to be used for field names.
 * @param {boolean} [props.isFirstInputFocus=false] - If true, the first input field will be focused on mount.
 * @param {string} props.message - The current message input by the user.
 * @param {Function} props.setMessage - Function to update the message state.
 *
 * @returns {JSX.Element} The rendered ContactUsFields component.
 */
const ContactUsFields = ({form, prefix = '', isFirstInputFocus = false, message, setMessage}) => {
    const fields = useContactUsFields({form, prefix})
    const nameRef = useRef()
    const {formatMessage} = useIntl()
    const [remainingCharacters, setRemainingCharacters] = useState(
        CONTACT_US_MESSAGE_MAXLENGTH - message.length
    )

    /**
     * Updates the message state and calculates the remaining characters.
     *
     * @param {string} value - The new value of the message input.
     */
    const handleInputChange = (value) => {
        setMessage(value)
        setRemainingCharacters(CONTACT_US_MESSAGE_MAXLENGTH - value.length)
    }

    useEffect(() => {
        if (isFirstInputFocus && nameRef.current) {
            nameRef.current.focus()
        }
    }, [])

    const {shipToCountry} = useShipToCountry()

    return (
        <>
            <Stack spacing={24} direction={{base: 'column', lg: 'row'}}>
                <Field {...fields.firstName} inputRef={nameRef} />
                <Field {...fields.lastName} />
            </Stack>
            <Stack spacing={24} direction={{base: 'column', lg: 'row'}}>
                <Field {...fields.email} />
            </Stack>
            <Stack spacing={24} direction={{base: 'column', lg: 'row'}}>
                <PhoneField {...fields.phone} defaultCountry={shipToCountry?.id} />
                <Field {...fields.orderNumber} />
            </Stack>
            <Stack spacing={24} direction={{base: 'column', lg: 'row'}}>
                <Field {...fields.country} />
                <Field {...fields.question} />
            </Stack>
            <Stack spacing={24} direction={{base: 'column', lg: 'row'}}>
                <FormControl variant={'floating'} isInvalid={false}>
                    <InputGroup>
                        <Textarea
                            onChange={(e) => handleInputChange(e.target.value)}
                            value={message}
                            maxLength={CONTACT_US_MESSAGE_MAXLENGTH}
                            placeholder={formatMessage({
                                id: 'contact_us.textarea.placeholder',
                                defaultMessage: 'Your message'
                            })}
                            rows={5}
                        />
                    </InputGroup>
                    <FormLabel>
                        {formatMessage({
                            defaultMessage: 'Your message (optional)',
                            id: 'contact_us.textarea.label'
                        })}
                    </FormLabel>
                    <FormHelperText>
                        <Text variant={'bodySmall'} color="darkGray" mt={4}>
                            {formatMessage(
                                {
                                    defaultMessage: `{remainingCharacters, plural, one {# character} other {# characters}} remaining of {messageMaxLength}`,
                                    id: 'contact_us.textarea.helper_text'
                                },
                                {
                                    remainingCharacters: remainingCharacters,
                                    messageMaxLength: CONTACT_US_MESSAGE_MAXLENGTH
                                }
                            )}
                        </Text>
                    </FormHelperText>
                </FormControl>
            </Stack>
            <Stack spacing={24} direction={{base: 'column', lg: 'row'}}>
                <Field {...fields.consent} />
            </Stack>
        </>
    )
}

ContactUsFields.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string,
    isFirstInputFocus: PropTypes.bool,
    message: PropTypes.string,
    setMessage: PropTypes.func
}

export default ContactUsFields
